/** Logo image validation helpers — PNG, JPG, JPEG only; SVG rejected. */

import { uploadsService } from "@/services/uploadsService";

export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

export interface LogoUploadResult {
  /** Persistent storage URL — save this to the API. */
  fileUrl: string;
  /** Temporary signed URL — use this for image preview in the UI. */
  signedUrl: string;
}

const ALLOWED_TYPES = new Set(["image/png", "image/jpeg", "image/jpg"]);
const ALLOWED_EXTENSIONS = new Set([".png", ".jpg", ".jpeg"]);

function fileExtension(file: File): string {
  if (!file.name.includes(".")) return "";
  return `.${file.name.split(".").pop()!.toLowerCase()}`;
}

function isSvgFile(file: File): boolean {
  const type = file.type.toLowerCase();
  const ext = fileExtension(file);
  return type === "image/svg+xml" || ext === ".svg";
}

function isAllowedImage(file: File): boolean {
  const type = file.type.toLowerCase();
  const ext = fileExtension(file);
  return ALLOWED_TYPES.has(type) || ALLOWED_EXTENSIONS.has(ext);
}

/**
 * Returns an error message when the file is invalid, or null when valid.
 * Allowed: PNG, JPG, JPEG. SVG is rejected. Max {@link LOGO_MAX_BYTES}.
 */
export function validateLogoFile(file: File): string | null {
  if (isSvgFile(file)) {
    return "SVG files are not supported. Please upload a PNG, JPG, or JPEG image.";
  }

  if (!isAllowedImage(file)) {
    return "Only PNG, JPG, and JPEG images are supported.";
  }

  if (file.size > LOGO_MAX_BYTES) {
    return `Image must be ${formatLogoMaxSize()} or smaller.`;
  }

  return null;
}

export function formatLogoMaxSize(): string {
  return `${LOGO_MAX_BYTES / (1024 * 1024)} MB`;
}

/** Upload content type token expected by POST /uploads (e.g. "png", "jpeg"). */
export function resolveUploadContentType(file: File): string {
  const ext = fileExtension(file).replace(/^\./, "");
  if (ext === "jpg") return "jpeg";
  return ext || "png";
}

const signedUrlByFileUrl = new Map<string, string>();

/** True when the URL already includes GCS V4 signing query params. */
export function isSignedGcsUrl(url: string): boolean {
  return /[?&]X-Goog-Signature=/i.test(url) || /[?&]X-Goog-Algorithm=/i.test(url);
}

function isDataUrl(url: string): boolean {
  return url.startsWith("data:");
}

function isGcsStorageUrl(url: string): boolean {
  return url.includes("storage.googleapis.com");
}

/** True when logoUrl is a legacy inline base64 value from the pre-upload API flow. */
export function isLegacyDataLogoUrl(logoUrl: string | null | undefined): boolean {
  const normalized = logoUrl?.trim();
  return Boolean(normalized && isDataUrl(normalized));
}

/** Remember a signed URL returned from POST /uploads for later display lookups. */
export function registerLogoSignedUrl(fileUrl: string, signedUrl: string): void {
  const file = fileUrl.trim();
  const signed = signedUrl.trim();
  if (file && signed) {
    signedUrlByFileUrl.set(file, signed);
  }
}

/**
 * URL suitable for `<img src>`.
 * Never returns base64 data URLs or raw unsigned GCS URLs (private bucket).
 * Prefers signed GCS URLs from the profile API or cached upload responses.
 */
export function resolveLogoDisplayUrl(logoUrl: string | null | undefined): string | null {
  const normalized = logoUrl?.trim();
  if (!normalized) return null;
  if (isDataUrl(normalized)) return null;
  if (isSignedGcsUrl(normalized)) return normalized;

  const cachedSigned = signedUrlByFileUrl.get(normalized);
  if (cachedSigned) return cachedSigned;

  if (isGcsStorageUrl(normalized)) return null;

  if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
    return normalized;
  }

  return null;
}

/** True when {@link resolveLogoDisplayUrl} would return a usable image URL. */
export function hasDisplayableLogoUrl(logoUrl: string | null | undefined): boolean {
  return Boolean(resolveLogoDisplayUrl(logoUrl));
}

/**
 * Persistent object URL for reach-out {@code imageUrl} payloads.
 * Strips GCS signing query params; prefers cached upload {@code fileUrl}.
 */
export function resolveReachOutImageFileUrl(url: string | null | undefined): string | null {
  const normalized = url?.trim();
  if (!normalized || isDataUrl(normalized)) return null;

  if (isSignedGcsUrl(normalized)) {
    for (const [fileUrl, signedUrl] of signedUrlByFileUrl.entries()) {
      if (signedUrl === normalized) return fileUrl;
    }
    if (isGcsStorageUrl(normalized)) {
      try {
        const parsed = new URL(normalized);
        parsed.search = "";
        parsed.hash = "";
        return parsed.href;
      } catch {
        return normalized.split("?")[0] || null;
      }
    }
    return null;
  }

  return normalized;
}

/**
 * URL suitable for API payloads (reach-out, etc.).
 * Prefers persistent `fileUrl`; accepts signed GCS URLs; rejects base64 data URLs.
 */
export function resolveLogoApiUrl(logoUrl: string | null | undefined): string | null {
  const normalized = logoUrl?.trim();
  if (!normalized || isDataUrl(normalized)) return null;
  if (isSignedGcsUrl(normalized)) {
    for (const [fileUrl, signedUrl] of signedUrlByFileUrl.entries()) {
      if (signedUrl === normalized) return fileUrl;
    }
    return normalized;
  }
  return normalized;
}

export async function uploadLogoFile(file: File): Promise<LogoUploadResult> {
  const contentType = resolveUploadContentType(file);
  const response = await uploadsService.upload(file, contentType);
  registerLogoSignedUrl(response.fileUrl, response.signedUrl);
  return {
    fileUrl: response.fileUrl,
    signedUrl: response.signedUrl,
  };
}

/**
 * Permanent GCS `fileUrl` suitable for PATCH payloads.
 * Returns `""` to clear the logo. Rejects base64 and signed URLs for persistence.
 */
export function normalizeLogoUrlForApi(logoUrl: string | null | undefined): string {
  const trimmed = logoUrl?.trim();
  if (!trimmed) return "";
  if (isDataUrl(trimmed)) return "";
  if (isSignedGcsUrl(trimmed)) {
    for (const [fileUrl, signedUrl] of signedUrlByFileUrl.entries()) {
      if (signedUrl === trimmed) return fileUrl;
    }
    return "";
  }
  if (isGcsStorageUrl(trimmed)) return trimmed;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  return "";
}
